import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #hiddenWhenSubmenuOpenAttr = 'data-hidden-when-submenu-open';
  #backgroundHiddenAttr = 'data-hidden-while-menu-open';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onSplitToggleClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onSplitToggleClick);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the split-toggle details element associated with a split-row control.
   * @param {Element} element
   * @returns {HTMLDetailsElement | null}
   */
  #getSplitToggleDetails(element) {
    const splitRow = element.closest('.menu-drawer__split-row');
    if (!splitRow) return null;

    const details = splitRow.querySelector('.menu-drawer__details--split-toggle');
    return details instanceof HTMLDetailsElement ? details : null;
  }

  /**
   * Get the submenu toggle for a details element (supports split-row markup).
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement | null}
   */
  #getSubmenuToggle(details) {
    const splitRow = details.closest('.menu-drawer__split-row');
    const toggle = splitRow?.querySelector(
      '.menu-drawer_split-row-parent > .menu-drawer__submenu-toggle'
    );

    if (toggle instanceof HTMLElement) return toggle;

    const summary = details.querySelector(':scope > summary:not(.menu-drawer__details-summary-placeholder)');
    return summary instanceof HTMLElement ? summary : null;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    const splitToggle = event.target.closest('.menu-drawer__submenu-toggle');
    if (splitToggle) {
      const splitDetails = this.#getSplitToggleDetails(splitToggle);
      if (splitDetails) return splitDetails;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Keep the external submenu toggle icon in sync with its paired details state.
   * @param {HTMLDetailsElement} details
   * @param {boolean} [isOpen] - Explicit open state; defaults to `details.open`.
   */
  #syncSubmenuToggleExpanded(details, isOpen = details.open) {
    const toggle = this.#getSubmenuToggle(details);
    if (!(toggle instanceof HTMLElement)) return;

    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  /**
   * Toggle inline accordion submenus where the summary lives outside the details element.
   * @param {MouseEvent} event
   */
  #onSplitToggleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest(
      '.menu-drawer_split-row-parent > .menu-drawer__submenu-toggle'
    );
    if (!(toggle instanceof HTMLElement) || toggle.hasAttribute('on:click')) return;

    const details = this.#getSplitToggleDetails(toggle);
    if (!details || details.querySelector('.menu-drawer__submenu')) return;

    event.preventDefault();

    details.open = !details.open;
    this.#syncSubmenuToggleExpanded(details);
  };

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    if (event instanceof Event && event.cancelable) {
      event.preventDefault();
    }

    const details = this.#getDetailsElement(event);
    const summary = this.#getSubmenuToggle(details);

    if (!summary) return;

    details.setAttribute('open', '');
    details.open = true;
    this.#syncSubmenuToggleExpanded(details, true);
    this.#setTopLevelItemsHidden(details !== this.refs.details);
    if (details === this.refs.details) {
      this.#setBackgroundContentHidden(true);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        if (details === this.refs.details) {
          // Trap within the drawer panel, not the outer <details>, so the hamburger
          // summary is excluded and Tab flows naturally through close button → menu items.
          const drawerPanel = details.querySelector('[data-header-drawer]');
          trapFocus(drawerPanel instanceof HTMLElement ? drawerPanel : details);
        } else {
          const drawerPanel = this.refs.details.querySelector('[data-header-drawer]');
          trapFocus(drawerPanel instanceof HTMLElement ? drawerPanel : this.refs.details);
          const firstInSubmenu = details.querySelector(
            '.menu-drawer__submenu button:not([disabled]), .menu-drawer__submenu a[href]'
          );
          if (firstInSubmenu instanceof HTMLElement) firstInSubmenu.focus();
        }
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = this.#getSubmenuToggle(details);

    if (!summary) return;

    this.#syncSubmenuToggleExpanded(details, false);
    details.classList.remove('menu-open');
    this.#setTopLevelItemsHidden(false);
    if (details === this.refs.details) {
      this.#setBackgroundContentHidden(false);
    }

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          const drawerPanel = this.refs.details.querySelector('[data-header-drawer]');
          trapFocus(drawerPanel instanceof HTMLElement ? drawerPanel : this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Hide top-level drawer triggers from SR/keyboard while a submenu overlay is open.
   * @param {boolean} hidden
   */
  #setTopLevelItemsHidden(hidden) {
    const topLevelMenuItems = this.querySelectorAll(
      '.menu-drawer__menu.has-submenu > li > a.menu-drawer__menu-item--mainlist, .menu-drawer__menu.has-submenu > li > .menu-drawer__split-row .menu-drawer_split-row-parent > a.menu-drawer__menu-item--mainlist, .menu-drawer__menu.has-submenu > li > .menu-drawer__split-row .menu-drawer_split-row-parent > .menu-drawer__submenu-toggle, .menu-drawer__menu.has-submenu > li > details > .menu-drawer__menu-item--mainlist, .menu-drawer__menu.has-submenu > li > details > .menu-drawer__submenu-toggle'
    );

    for (const item of topLevelMenuItems) {
      if (!(item instanceof HTMLElement)) continue;

      if (hidden) {
        item.setAttribute(this.#hiddenWhenSubmenuOpenAttr, 'true');
        item.setAttribute('aria-hidden', 'true');
        item.dataset.previousTabindex = item.getAttribute('tabindex') ?? '';
        item.setAttribute('tabindex', '-1');
      } else if (item.hasAttribute(this.#hiddenWhenSubmenuOpenAttr)) {
        item.removeAttribute(this.#hiddenWhenSubmenuOpenAttr);
        item.removeAttribute('aria-hidden');

        const previousTabindex = item.dataset.previousTabindex ?? '';
        if (previousTabindex === '') {
          item.removeAttribute('tabindex');
        } else {
          item.setAttribute('tabindex', previousTabindex);
        }
        delete item.dataset.previousTabindex;
      }
    }
  }

  /**
   * Hide all root body siblings from AT while the main menu drawer is open.
   * This prevents screen readers from traversing background page content behind the overlay.
   * @param {boolean} hidden
   */
  #setBackgroundContentHidden(hidden) {
    const bodyChildren = Array.from(document.body.children);

    for (const child of bodyChildren) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.contains(this)) continue;

      if (hidden) {
        child.setAttribute(this.#backgroundHiddenAttr, 'true');
        child.dataset.previousAriaHidden = child.getAttribute('aria-hidden') ?? '';
        child.dataset.previousInert = child.hasAttribute('inert') ? 'true' : 'false';
        child.setAttribute('aria-hidden', 'true');
        child.setAttribute('inert', '');
      } else if (child.hasAttribute(this.#backgroundHiddenAttr)) {
        child.removeAttribute(this.#backgroundHiddenAttr);

        const previousAriaHidden = child.dataset.previousAriaHidden ?? '';
        if (previousAriaHidden === '') {
          child.removeAttribute('aria-hidden');
        } else {
          child.setAttribute('aria-hidden', previousAriaHidden);
        }

        if (child.dataset.previousInert === 'true') {
          child.setAttribute('inert', '');
        } else {
          child.removeAttribute('inert');
        }

        delete child.dataset.previousAriaHidden;
        delete child.dataset.previousInert;
      }
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.open = false;
  element.removeAttribute('open');

  const toggle =
    element.closest('.menu-drawer__split-row')?.querySelector(
      '.menu-drawer_split-row-parent > .menu-drawer__submenu-toggle'
    ) ?? element.querySelector('summary:not(.menu-drawer__details-summary-placeholder)');

  toggle?.setAttribute('aria-expanded', 'false');
}
